import { FC, ReactNode } from 'react';
import {
  ActivityType,
  AssessmentSchemaType,
  NotificationDataFragment,
  useAcceptInvitationMutation,
  useDeclineCollaboratorInvitationMutation,
} from '../../generated/graphql';
import Avatar from '../components/Avatar';
import Button from '../components/Button';
import {
  ProviderAnalyticsEvent,
  trackProviderEvent,
} from '../../lib/analytics';
import { openInNewTab } from './url';
import { formatCurrency } from './currency';
import { getTimeStamp } from './time';

// TODO: Can't figure out a way to get these auto-generated from the GraphQL layer
// but it definitely would be nice
export enum ProviderUserNotificationTypename {
  ProviderUserNotificationPatientAssignment = 'ProviderUserNotificationPatientAssignment',
  ProviderUserNotificationStorefrontSignUp = 'ProviderUserNotificationStorefrontSignUp',
  ProviderUserNotificationActivation = 'ProviderUserNotificationActivation',
  ProviderUserNotificationActivityCompleted = 'ProviderUserNotificationActivityCompleted',
  ProviderUserNotificationAssessmentCompleted = 'ProviderUserNotificationAssessmentCompleted',
  ProviderUserNotificationComment = 'ProviderUserNotificationComment',
  ProviderUserNotificationInvoicePaid = 'ProviderUserNotificationInvoicePaid',
  ProviderUserNotificationSessionBooked = 'ProviderUserNotificationSessionBooked',
  ProviderUserNotificationFormNeedsProviderSignature = 'ProviderUserNotificationFormNeedsProviderSignature',
  ProviderUserNotificationPendingProviderMembership = 'ProviderUserNotificationPendingProviderMembership',
}

interface HandleNotificationClickParams {
  notification: NotificationDataFragment;
  markProviderUserNotificationRead: any;
  refreshAuthedProviderUserNotifications: any;
  navigate: any;
  skipMarkRead?: boolean;
  newTab?: boolean;
}

export const FormSignatureContent: FC<{
  formName: string;
  patientName: string;
}> = ({ formName, patientName }) => (
  <span className="font-normal">
    It's your turn to sign <span className="font-medium">{formName}</span> for{' '}
    <span className="font-medium">{patientName}</span>.
  </span>
);

export const NotificationAvatar: FC<{
  notification: NotificationDataFragment;
  size?: 'small' | 'medium' | 'medium-plus' | 'large';
  className?: string;
}> = ({ notification, size = 'medium', className }) => {
  let name = '';
  let imageUrl: string | undefined;

  if ('patientName' in notification && notification.patientName) {
    name = notification.patientName;
    imageUrl =
      'patientProfileImageMedia' in notification
        ? notification.patientProfileImageMedia?.url
        : undefined;
  } else if (
    notification.__typename ===
      'ProviderUserNotificationPendingProviderMembership' &&
    notification.invitedByProviderSquareLogoMedia
  ) {
    name = notification.invitedByProviderName || '';
    imageUrl = notification.invitedByProviderSquareLogoMedia.url;
  }

  if (!name && !imageUrl) {
    return null;
  }

  return (
    <Avatar size={size} name={name} imageUrl={imageUrl} className={className} />
  );
};

export const useNotificationActionButtons = (
  notification: NotificationDataFragment,
  refreshAuthedProviderUserNotifications: () => Promise<void>,
) => {
  const [acceptInvitation, { loading: acceptLoading }] =
    useAcceptInvitationMutation();
  const [declineInvitation, { loading: declineLoading }] =
    useDeclineCollaboratorInvitationMutation();

  const handleAccept = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (
        notification.__typename ===
        'ProviderUserNotificationPendingProviderMembership'
      ) {
        const result = await acceptInvitation({
          variables: {
            input: {
              membershipId: notification.membershipId,
            },
          },
        });

        if (result.data?.acceptCollaboratorInvitation.success) {
          await refreshAuthedProviderUserNotifications();
        }
      }
    } catch (err) {
      console.error('Failed to accept invitation:', err);
    }
  };

  const handleDecline = async (e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      if (
        notification.__typename ===
        'ProviderUserNotificationPendingProviderMembership'
      ) {
        const result = await declineInvitation({
          variables: {
            input: {
              membershipId: notification.membershipId,
            },
          },
        });

        if (result.data?.declineCollaboratorInvitation.success) {
          await refreshAuthedProviderUserNotifications();
        }
      }
    } catch (err) {
      console.error('Failed to decline invitation:', err);
    }
  };

  if (
    notification.__typename ===
      'ProviderUserNotificationPendingProviderMembership' &&
    notification.membershipStatus === 'INVITED'
  ) {
    return (
      <div className="flex gap-1">
        <Button
          size="extra-small"
          theme="primary"
          title="Accept"
          onClick={handleAccept}
          disabled={acceptLoading || declineLoading}
        />
        <Button
          size="extra-small"
          theme="alt"
          title="Decline"
          onClick={handleDecline}
          disabled={acceptLoading || declineLoading}
        />
      </div>
    );
  }

  return null;
};

export const getNotficationContent = (
  notification: NotificationDataFragment,
): ReactNode | null => {
  let NotificationContent: ReactNode = null;

  switch (notification.__typename) {
    case ProviderUserNotificationTypename.ProviderUserNotificationFormNeedsProviderSignature:
      NotificationContent = (
        <FormSignatureContent
          formName={notification.formName}
          patientName={notification.patientName}
        />
      );
      break;
    case ProviderUserNotificationTypename.ProviderUserNotificationPatientAssignment:
      NotificationContent = (
        <span className="font-normal">
          You will now be working with{' '}
          <span className="font-medium">{notification.patientName}</span>!
        </span>
      );
      break;
    case ProviderUserNotificationTypename.ProviderUserNotificationStorefrontSignUp:
      NotificationContent = (
        <span className="font-normal">
          <span className="font-medium">{notification.patientName}</span> has
          signed up to work with you on Homecoming.
        </span>
      );
      break;
    case ProviderUserNotificationTypename.ProviderUserNotificationActivation:
      NotificationContent = (
        <span className="font-normal">
          <span className="font-medium">{notification.patientName}</span> has
          activated their account on the Homecoming mobile app.
        </span>
      );
      break;
    case ProviderUserNotificationTypename.ProviderUserNotificationActivityCompleted:
      const isActivityPdfForm =
        notification.activityType === ActivityType.PdfForm;
      const isAction = notification.activityType === ActivityType.Action;
      NotificationContent = (
        <span className="font-normal">
          <span className="font-medium">{notification.patientName}</span>{' '}
          {isAction
            ? 'completed an action'
            : `completed the ${isActivityPdfForm ? 'PDF form' : 'activity'}`}
          {!isAction && (
            <span className="font-medium"> {notification.activityName}</span>
          )}
        </span>
      );
      break;
    case ProviderUserNotificationTypename.ProviderUserNotificationAssessmentCompleted:
      const isForm =
        notification.assessmentSchemaType === AssessmentSchemaType.Custom;
      NotificationContent = (
        <span className="font-normal">
          <span className="font-medium">{notification.patientName}</span>{' '}
          completed the {isForm ? 'form' : 'assessment'}{' '}
          <span className="font-medium">{notification.assessmentName}</span>.
        </span>
      );
      break;
    case ProviderUserNotificationTypename.ProviderUserNotificationComment:
      if (notification.programFollowUpId) {
        NotificationContent = (
          <span className="font-normal">
            <span className="font-medium">{notification.patientName}</span>{' '}
            commented on their{' '}
            <span className="font-medium">
              {getTimeStamp(
                notification.programFollowUpCreatedAt,
                false,
                false,
                false,
              )}{' '}
              follow-up
            </span>
            .
          </span>
        );
      } else {
        NotificationContent = (
          <span className="font-normal">
            <span className="font-medium">{notification.patientName}</span> sent
            you a message.
          </span>
        );
      }
      break;
    case ProviderUserNotificationTypename.ProviderUserNotificationInvoicePaid:
      NotificationContent = (
        <span className="font-normal">
          <span className="font-medium">{notification.patientName}</span> paid
          your invoice for{' '}
          <span className="font-medium">
            {formatCurrency(
              notification.programInvoiceAmount,
              notification.programInvoiceCurrency,
            )}
          </span>
          .
        </span>
      );
      break;
    case ProviderUserNotificationTypename.ProviderUserNotificationSessionBooked:
      NotificationContent = (
        <>
          <span className="font-medium">{notification.patientName}</span>{' '}
          scheduled a{' '}
          {notification.calendlyEventTypeName ? (
            <span className="font-medium">
              {notification.calendlyEventTypeName}
            </span>
          ) : (
            'session'
          )}
          .
        </>
      );
      break;
    case ProviderUserNotificationTypename.ProviderUserNotificationPendingProviderMembership:
      if (notification.membershipStatus === 'INVITED') {
        NotificationContent = (
          <span className="font-normal">
            <span className="font-medium">{notification.invitedByName}</span>{' '}
            invited you to join{' '}
            <span className="font-medium">
              {notification.invitedByProviderName}
            </span>{' '}
            as a{' '}
            <span className="font-medium">
              {notification.role.toLowerCase()}
            </span>
            .
          </span>
        );
      } else if (
        notification.membershipStatus === 'ACTIVE' ||
        notification.membershipStatus === 'REVOKED'
      ) {
        NotificationContent = (
          <span className="font-normal">
            You've accepted the invitation to join{' '}
            <span className="font-medium">
              {notification.invitedByProviderName}
            </span>{' '}
            as a{' '}
            <span className="font-medium">
              {notification.role.toLowerCase()}
            </span>
            .
          </span>
        );
      }
      break;
    default:
      break;
  }
  return NotificationContent;
};

export const handleNotificationClick = async (
  params: HandleNotificationClickParams,
): Promise<void> => {
  const {
    markProviderUserNotificationRead,
    notification,
    refreshAuthedProviderUserNotifications,
    navigate,
    skipMarkRead = false,
    newTab = false,
  } = params;
  // TODO: This works but blocks the UI.
  // We can use a cache to update optimistically in a future pass.
  if (!notification.readAt && !skipMarkRead) {
    await markProviderUserNotificationRead({
      variables: {
        providerUserNotificationId: notification.id,
        archivedNotification: false, // Always false for row/CTA/NotificationsPanel
      },
    });
    await refreshAuthedProviderUserNotifications();
  }

  const openUrl = newTab ? openInNewTab : navigate;

  switch (notification.__typename) {
    case ProviderUserNotificationTypename.ProviderUserNotificationFormNeedsProviderSignature:
      openUrl(`/provider-form/${notification.programAssessmentId}`);
      break;
    case ProviderUserNotificationTypename.ProviderUserNotificationStorefrontSignUp:
    case ProviderUserNotificationTypename.ProviderUserNotificationActivation:
      openUrl(`/clients/${notification.programId}`);
      break;
    case ProviderUserNotificationTypename.ProviderUserNotificationPatientAssignment:
      openUrl(`/clients/${notification.programId}/account`);
      break;
    case ProviderUserNotificationTypename.ProviderUserNotificationActivityCompleted:
      const isActivityPdfForm =
        notification.activityType === ActivityType.PdfForm;
      const activityPath = isActivityPdfForm
        ? 'documents-and-files'
        : 'activities';
      openUrl(`/clients/${notification.programId}/${activityPath}`);
      break;
    case ProviderUserNotificationTypename.ProviderUserNotificationAssessmentCompleted:
      openUrl(`/clients/${notification.programId}/documents-and-files`);
      break;
    case ProviderUserNotificationTypename.ProviderUserNotificationComment:
      if (notification.programFollowUpId) {
        openUrl(
          `/clients/${notification.programId}/follow-ups/${notification.programFollowUpId}`,
        );
      } else {
        openUrl(`/clients/${notification.programId}/messages`);
      }
      break;
    case ProviderUserNotificationTypename.ProviderUserNotificationInvoicePaid:
      openUrl(`/payments`);
      break;
    case ProviderUserNotificationTypename.ProviderUserNotificationSessionBooked:
      openUrl(`/calendar`);
      break;
    default:
      break;
  }
  trackProviderEvent(ProviderAnalyticsEvent.NotificationClicked, {
    notificationType: notification.type,
  });
};
